import { Fragment, memo, useCallback, useEffect, useRef, useState } from "react";
import { useGlobalFunction } from "../../Context/GlobalFunctionContext";
import { isValidArray, readCarouselPosition } from "../../Layout/Common/common";
import { ProductCard } from "../../Layout/Common/productCard";
import { CarouselEndCard } from "../../Layout/Common/carouselEndCard";
import { OwlCarousel as ReactOwlCarousel } from "../../Layout/Common/owlCarousel";
import { useAuth } from "../../Context/AuthContext";

const SUGGESTION_AUTOPLAY_MS = 5000;
const MAX_VISIBLE_ITEMS = 5;

const SuggestionCarousel = memo(({ products, owlRef, onPositionChange, onRestart }) => {
  const canRotate = products.length > MAX_VISIBLE_ITEMS;

  return (
    <ReactOwlCarousel
      ref={owlRef}
      className="relatedProduct-list carousel-equal-height"
      loop={false}
      rewind={false}
      items={MAX_VISIBLE_ITEMS}
      dots={true}
      autoplay={canRotate}
      autoplayTimeout={SUGGESTION_AUTOPLAY_MS}
      autoplayHoverPause={true}
      onInitialized={onPositionChange}
      onTranslated={onPositionChange}
      onResized={onPositionChange}
      responsive={{
        0: { items: 1 },
        430: { items: 2 },
        540: { items: 3 },
        991: { items: MAX_VISIBLE_ITEMS },
      }}
    >
      {products.map((row, index) => (
        <div className="item mt-3" key={row.productId ?? index}>
          <ProductCard data={row} />
        </div>
      ))}
      {canRotate && (
        <div className="item mt-3" key="carousel-end">
          <CarouselEndCard onRestart={onRestart} />
        </div>
      )}
    </ReactOwlCarousel>
  );
});

export const SuggestionProducts = ({ params }) => {
  const [pageData, setPageData] = useState(null);
  const [familyId, setFamilyId] = useState(null);
  const [productList, setProductList] = useState(null);
  const [nav, setNav] = useState({ atStart: true, atEnd: false });
  const owlRef = useRef();
  const { getSuggestionProducts } = useGlobalFunction();
  useEffect(() => {
    if (params) {
      let details = params.familyDetails;
      if (details.isFamily == false) {
        setPageData(details);
        setFamilyId(details.familyId);
        loadSuggestions(details.familyId, details.productId);
      }
    }
  }, [params]);
  const loadSuggestions = async (familyId, productId) => {
    let results = await getSuggestionProducts(familyId, productId);
    let suggestionProductList = results;
    if (isValidArray(results)) {
      if (productId) {
        suggestionProductList = results.filter((a) => parseInt(a.productId) != parseInt(productId));
      }

      setProductList(suggestionProductList.slice(0, 12));
    }
  };

  const handlePosition = useCallback((event) => {
    const next = readCarouselPosition(event);
    if (!next) return;
    setNav((prev) => (prev.atStart === next.atStart && prev.atEnd === next.atEnd ? prev : next));
  }, []);

  const goStart = useCallback(() => owlRef.current?.to(0, 300), []);
  const goPrev = () => owlRef.current?.prev();
  const goNext = () => owlRef.current?.next();

  return (
    <Fragment>
      {pageData && familyId && isValidArray(productList) && (
        <section className="proSection relatedProduct-section">
          <div className="container-fluid container-lg">
            <div className="row">
              <div className="col-lg-12">
                <div className="subcategory-title">
                  <h4>Related Products</h4>
                </div>
              </div>
            </div>
            <div className="row row-spacing">
              <div className="col-lg-12">
                <div className="relatedProduct-wrapper">
                  <button
                    type="button"
                    className={"homeCustom-nav product-left-arrow" + (nav.atStart ? " is-disabled" : "")}
                    onClick={goPrev}
                    disabled={nav.atStart}
                    aria-label="Previous related products"
                  >
                    <i className="fa-solid fa-angle-left" />
                  </button>

                  <SuggestionCarousel
                    products={productList}
                    owlRef={owlRef}
                    onPositionChange={handlePosition}
                    onRestart={goStart}
                  />

                  <button
                    type="button"
                    className={"homeCustom-nav product-right-arrow" + (nav.atEnd ? " is-disabled" : "")}
                    onClick={goNext}
                    disabled={nav.atEnd}
                    aria-label="Next related products"
                  >
                    <i className="fa-solid fa-angle-right" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </Fragment>
  );
};
